import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MartProductCard from './MartProductCard'; 
import './MartCategories.css';

const MART_API_BASE = 'https://api.atrmart.in/martapi';

const MartCategories = ({ cart, addToCart, user, onLoginClick }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const categoryId = queryParams.get('id');
  const categoryName = queryParams.get('name') || 'Products';
  const initialSubId = queryParams.get('sub_id');

  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubId, setSelectedSubId] = useState(initialSubId || null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialSubId) {
      setSelectedSubId(initialSubId);
    } else {
      setSelectedSubId(null);
    }
  }, [initialSubId]);

  // Fetch sibling horizontal navigation header subcategory pills
  useEffect(() => {
    const fetchSubCategoriesData = async () => {
      if (!categoryId) return;
      try {
        const response = await axios.get(`${MART_API_BASE}/subcategories`, {
          params: { category_id: categoryId }
        });
        
        let extractedSub = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            extractedSub = response.data;
          } else if (Array.isArray(response.data.data)) {
            extractedSub = response.data.data;
          } else if (Array.isArray(response.data.sub_categories)) {
            extractedSub = response.data.sub_categories;
          }
        }
        setSubCategories(extractedSub);
      } catch (err) {
        console.error("Error fetching subcategories bar payload:", err);
        setSubCategories([]);
      }
    };
    
    fetchSubCategoriesData();
  }, [categoryId]);

  // Fetch items based on hierarchy criteria
  const fetchFilteredProducts = useCallback(async () => {
    if (!categoryId) {
      setError("No category selected.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const params = { category_id: categoryId };
      if (selectedSubId) {
        params.subcategory_id = selectedSubId;
      }

      const response = await axios.get(`${MART_API_BASE}/products`, { params });
      
      let extractedProducts = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          extractedProducts = response.data;
        } else if (Array.isArray(response.data.data)) {
          extractedProducts = response.data.data;
        } else if (Array.isArray(response.data.products)) {
          extractedProducts = response.data.products;
        }
      }
      
      setProducts(extractedProducts);
    } catch (err) {
      console.error("Error filtering mart product array view:", err);
      setError("Failed to load products for this selection.");
    } finally {
      setLoading(false);
    }
  }, [categoryId, selectedSubId]);

  useEffect(() => {
    fetchFilteredProducts();
  }, [fetchFilteredProducts]);

  const handleSubCategoryPillClick = (subId) => {
    const targetSubId = selectedSubId === subId ? null : subId;
    setSelectedSubId(targetSubId);
    
    if (targetSubId) {
      navigate(`/martproducts?id=${categoryId}&name=${encodeURIComponent(categoryName)}&sub_id=${subId}`, { replace: true });
    } else {
      navigate(`/martproducts?id=${categoryId}&name=${encodeURIComponent(categoryName)}`, { replace: true });
    }
  };

  return (
    <div className="m-category-view-container" style={{ padding: '20px' }}>
      <div className="m-category-header" style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 className="m-category-title" style={{ textTransform: 'uppercase' }}>EXPLORE {decodeURIComponent(categoryName)}</h2>
          <div className="m-section-divider" style={{ width: '60px', height: '3px', background: '#00f2fe', marginTop: '5px' }} />
        </div>
        <button className="m-back-home-btn" onClick={() => navigate('/mart')} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          ← Back Home
        </button>
      </div>

      {subCategories && subCategories.length > 0 && (
        <div className="m-subcategories-pill-wrapper" style={{ display: 'flex', gap: '10px', marginBottom: '25px', overflowX: 'auto', paddingBottom: '10px' }}>
          <button 
            className={`m-sub-pill ${selectedSubId === null ? 'active-pill' : ''}`}
            onClick={() => handleSubCategoryPillClick(null)}
            style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid #cbd5e1', background: selectedSubId === null ? '#00f2fe' : '#fff', cursor: 'pointer' }}
          >
            All Items
          </button>
          {subCategories.map((sub) => (
            <button
              key={sub.id}
              className={`m-sub-pill ${Number(selectedSubId) === Number(sub.id) ? 'active-pill' : ''}`}
              onClick={() => handleSubCategoryPillClick(sub.id)}
              style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid #cbd5e1', background: Number(selectedSubId) === Number(sub.id) ? '#00f2fe' : '#fff', cursor: 'pointer' }}
            >
              {sub.name}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="m-status-message" style={{ textAlign: 'center', padding: '20px' }}>
          <p>Filtering catalog inventories...</p>
        </div>
      )}

      {error && !loading && (
        <div className="m-status-message m-error-text" style={{ textAlign: 'center', color: 'red' }}>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && products.length > 0 && (
        <div className="m-products-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {products.map((product) => (
            <MartProductCard
              key={product.id}
              item={product}
              navigate={navigate}
              addToCart={addToCart}
              cart={cart}
              user={user}
              onLoginClick={onLoginClick}
            />
          ))}
        </div>
      )}

      {!loading && !error && products.length === 0 && (
        <div className="m-empty-box" style={{ textAlign: 'center', padding: '40px' }}>
          <span className="m-empty-icon" style={{ fontSize: '3rem' }}>🛍️</span>
          <h3>No items found</h3>
          <p>We are currently uploading stock to this subcategory index tier.</p>
        </div>
      )}
    </div>
  );
};

export default MartCategories;